import React, { useEffect, useState } from 'react';
import CalendarPicker from './CalendarPicker';
import { FiCalendar } from 'react-icons/fi';
import './RequestFormModal.css';

export default function RequestFormModal({ open, onClose, endpoint, onSuccess }) {
  const [form, setForm] = useState({
    // Identity
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Org
    organization: '',
    website: '',
    // Event basics
    eventName: '',
    purpose: '',
    eventDate: '',
    eventTime: '',
    eventDuration: '',
    // Address
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    // Attendance
    attendees: '',
    pointOfContact: 'Yes',
    // Special requests
    specialRequests: '',
  });
  const [status, setStatus] = useState({ state: 'idle', error: '' });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [recaptcha, setRecaptcha] = useState({ enabled: false, siteKey: '', loaded: false });

  const isConfigured = Boolean(endpoint && endpoint.startsWith('http'));

  // When the modal opens, ensure success screen is cleared so the form is visible
  // Also lock body scroll
  useEffect(() => {
    if (open) {
      if (status.state !== 'idle') {
        setStatus({ state: 'idle', error: '' });
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Listen for a global reset event (used by Start Over) to clear the form and status
  useEffect(() => {
    const onReset = () => {
      setForm({
        firstName: '', lastName: '', email: '', phone: '',
        organization: '', website: '',
        eventName: '', purpose: '', eventDate: '', eventTime: '', eventDuration: '',
        address1: '', address2: '', city: '', state: '', zip: '',
        attendees: '', pointOfContact: 'Yes', specialRequests: '',
      });
      setStatus({ state: 'idle', error: '' });
    };
    window.addEventListener('sj-reset-request', onReset);
    return () => window.removeEventListener('sj-reset-request', onReset);
  }, []);

  // Derive the WordPress base origin from the endpoint to fetch settings
  const getWpBase = () => {
    try {
      const u = new URL(endpoint);
      return `${u.protocol}//${u.host}`;
    } catch {
      return '';
    }
  };

  const formatDateDisplay = (isoDate) => {
    if (!isoDate) return '';
    try {
      // Parse YYYY-MM-DD
      const [y, m, d] = isoDate.split('-').map(Number);
      return `${m}/${d}/${y}`;
    } catch {
      return isoDate;
    }
  };

  // Load reCAPTCHA script once
  const ensureRecaptcha = (siteKey) =>
    new Promise((resolve) => {
      if (!siteKey) return resolve(false);
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        return resolve(true);
      }
      const existing = document.querySelector('script[data-sj-rec="1"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        return;
      }
      const s = document.createElement('script');
      s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-sj-rec', '1');
      s.onload = () => resolve(true);
      document.head.appendChild(s);
    });

  // Fetch WP settings to get reCAPTCHA site key
  useEffect(() => {
    // Only attempt to fetch settings when the modal is open
    if (!open) return;
    const wpBase = getWpBase();
    if (!wpBase) return;
    let aborted = false;
    fetch(`${wpBase}/wp-json/sj/v1/settings`, { method: 'GET', credentials: 'omit' })
      .then((r) => (r && r.ok ? r.json() : null))
      .then(async (data) => {
        if (aborted || !data) return;
        const siteKey = data?.recaptcha?.siteKey || '';
        const enabled = Boolean(data?.recaptcha?.enabled && siteKey);
        setRecaptcha((prev) => ({ ...prev, enabled, siteKey }));
        if (enabled && siteKey) {
          await ensureRecaptcha(siteKey);
          if (!aborted) setRecaptcha((prev) => ({ ...prev, loaded: true }));
        }
      })
      .catch(() => {});
    return () => { aborted = true; };
    /* eslint-disable-next-line */
  }, [endpoint, open]);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Consider the form "dirty" when any field has a value or pointOfContact differs from default
  const dirty = (
    !!(form.firstName || form.lastName || form.email || form.phone || form.organization || form.website ||
       form.eventName || form.purpose || form.eventDate || form.eventTime || form.eventDuration || form.address1 || form.address2 ||
       form.city || form.state || form.zip || form.attendees || form.specialRequests) ||
    form.pointOfContact !== 'Yes'
  );

  const handleCancel = (e) => {
    e.preventDefault();
    setForm({
      firstName: '', lastName: '', email: '', phone: '',
      organization: '', website: '',
      eventName: '', purpose: '', eventDate: '', eventTime: '', eventDuration: '',
      address1: '', address2: '', city: '', state: '', zip: '',
      attendees: '', pointOfContact: 'Yes', specialRequests: '',
    });
    setStatus({ state: 'idle', error: '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isConfigured) return;
    setStatus({ state: 'submitting', error: '' });
    try {
      // Assemble payload; include reCAPTCHA token if enabled
      // Backward-compat fields expected by WP plugin: name, eventDate, guestCount, location, message
      const fullName = `${(form.firstName || '').trim()} ${(form.lastName || '').trim()}`.trim();
      const locParts = [form.address1, form.address2, form.city, form.state, form.zip].map((s) => (s || '').trim()).filter(Boolean);
      const combinedLocation = locParts.join(', ');
      // Format date as MM/DD/YY for email readability, but keep ISO for consistency if needed
      let eventDateText = form.eventDate;
      if (form.eventDate && /\d{4}-\d{2}-\d{2}/.test(form.eventDate)) {
        const [y, m, d] = form.eventDate.split('-');
        eventDateText = `${m}/${d}/${y.slice(-2)}`;
      }
      const payload = {
        ...form,
        // legacy fields
        name: fullName || `${form.firstName || ''}`.trim(),
        guestCount: form.attendees,
        location: combinedLocation,
        message: form.specialRequests,
        eventDate: eventDateText,
      };
      if (recaptcha.enabled && recaptcha.siteKey) {
        const action = 'request_form';
        try {
          await ensureRecaptcha(recaptcha.siteKey);
          await new Promise((res) => window.grecaptcha.ready(res));
          const token = await window.grecaptcha.execute(recaptcha.siteKey, { action });
          payload.recaptchaToken = token;
          payload.recaptchaAction = action;
        } catch (err) {
          throw new Error('Could not complete reCAPTCHA. Please try again.');
        }
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
        credentials: 'omit',
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `Request failed (${res.status})`);
      }
      setStatus({ state: 'success', error: '' });
      // Persist progress and auto-advance after a short delay
      try { localStorage.setItem('sjProgress', 'request'); } catch (_e) {}
      if (typeof onSuccess === 'function') {
        setTimeout(() => { try { onSuccess(); } catch (_e) {} }, 600);
      }
    } catch (err) {
      setStatus({ state: 'error', error: err.message || 'Something went wrong' });
    }
  };

  if (!open) return null;

  return (
    <div className="rfm-overlay" role="dialog" aria-modal="true" aria-label="Request Sweet Justice">
      <div className="rfm-modal">
        <div className="rfm-header">
          <h3 className="rfm-title">Request Sweet Justice</h3>
          <button type="button" className="rfm-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        
        {status.state === 'success' ? (
          <div className="rfm-success">
            <p>Thanks! Your request has been received. We'll get back to you shortly.</p>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form className="rfm-form" onSubmit={submit}>
            <div className="rfm-content">
              {!isConfigured && (
                <p className="rfm-config-warning">
                  WordPress endpoint is not configured. Please set it before submitting.
                </p>
              )}
              <div className="rfm-grid">
                <label className="rfm-field">
                  <span>First Name</span>
                  <input name="firstName" value={form.firstName} onChange={update} required autoComplete="given-name" />
                </label>
                <label className="rfm-field">
                  <span>Last Name</span>
                  <input name="lastName" value={form.lastName} onChange={update} required autoComplete="family-name" />
                </label>
                <label className="rfm-field">
                  <span>Email</span>
                  <input name="email" type="email" value={form.email} onChange={update} required autoComplete="email" />
                </label>
                <label className="rfm-field">
                  <span>Phone</span>
                  <input name="phone" value={form.phone} onChange={update} autoComplete="tel" />
                </label>
                <label className="rfm-field">
                  <span>Name of Charity or Organization</span>
                  <input name="organization" value={form.organization} onChange={update} autoComplete="organization" />
                </label>
                <label className="rfm-field">
                  <span>Your Website (if applicable)</span>
                  <input name="website" type="text" placeholder="e.g. goldlaw.com" value={form.website} onChange={update} autoComplete="url" />
                </label>
                <label className="rfm-field">
                  <span>Name of Your Event</span>
                  <input name="eventName" value={form.eventName} onChange={update} autoComplete="off" />
                </label>
                <label className="rfm-field">
                  <span>Purpose of Event</span>
                  <input name="purpose" value={form.purpose} onChange={update} autoComplete="off" />
                </label>
                <label className="rfm-field">
                  <span>Event Date</span>
                  <div className="rfm-date-input-wrapper" onClick={() => setCalendarOpen(true)}>
                    <input
                      name="eventDate"
                      value={formatDateDisplay(form.eventDate)}
                      readOnly
                      placeholder="Select a date..."
                      className="rfm-date-input"
                    />
                    <FiCalendar className="rfm-date-icon" />
                  </div>
                </label>
                <label className="rfm-field">
                  <span>Time of Event</span>
                  <input name="eventTime" type="time" value={form.eventTime} onChange={update} autoComplete="off" />
                </label>
                <label className="rfm-field">
                  <span>Duration of Event</span>
                  <input name="eventDuration" value={form.eventDuration} onChange={update} placeholder="e.g. 2 hours" autoComplete="off" />
                </label>
                <label className="rfm-field rfm-col-span">
                  <span>Event Address</span>
                  <input name="address1" value={form.address1} onChange={update} placeholder="Street address" autoComplete="address-line1" />
                </label>
                <label className="rfm-field rfm-col-span">
                  <span>Address Line 2 (optional)</span>
                  <input name="address2" value={form.address2} onChange={update} placeholder="Apt, suite, unit, etc." autoComplete="address-line2" />
                </label>
                <label className="rfm-field">
                  <span>City</span>
                  <input name="city" value={form.city} onChange={update} autoComplete="address-level2" />
                </label>
                <label className="rfm-field">
                  <span>State</span>
                  <input name="state" value={form.state} onChange={update} autoComplete="address-level1" />
                </label>
                <label className="rfm-field">
                  <span>Zip Code</span>
                  <input name="zip" value={form.zip} onChange={update} autoComplete="postal-code" />
                </label>
                <label className="rfm-field">
                  <span>Expected Number of Attendees</span>
                  <input name="attendees" type="number" min="1" value={form.attendees} onChange={update} autoComplete="off" />
                </label>
                <label className="rfm-field">
                  <span>Are you the point of contact?</span>
                  <select name="pointOfContact" value={form.pointOfContact} onChange={update} autoComplete="off">
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </label>
                <label className="rfm-field rfm-col-span">
                  <span>Do you have any special requests?</span>
                  <textarea name="specialRequests" rows={4} value={form.specialRequests} onChange={update} autoComplete="off" />
                </label>
              </div>
              {status.state === 'error' && (
                <p className="rfm-error">{status.error}</p>
              )}
            </div>
            <div className="rfm-actions">
              <button type="button" className="btn btn-outline" onClick={handleCancel} disabled={!dirty}>Cancel</button>
              <button type="submit" className="btn btn-accent" disabled={!isConfigured || status.state === 'submitting'}>
                {status.state === 'submitting' ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          </form>
        )}
      </div>
      <CalendarPicker
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        endpoint={endpoint}
        selectedDate={form.eventDate}
        onChange={(d) => setForm(f => ({ ...f, eventDate: d }))}
      />
    </div>
  );
}
